import { VideoData } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { applySceneWatermarkEdit } from './scene-watermark-edit';
import { VideoStateTool } from './VideoStateTool';

export const legacySetWatermarkSchema = z.object({
    scene_index: z.number().describe('The scene index (0, 1, 2, etc.)'),
    visibility: z
        .boolean()
        .optional()
        .describe(
            'The visibility of the watermark. Provide this to change watermark visibility (show/hide).',
        ),
    watermark_source_index: z
        .int()
        .optional()
        .describe(
            'The index of the watermark to apply as watermark. Must be an index from the template watermark sources.',
        ),
    position: z
        .enum(VideoData.WATERMARK_POSITIONS)
        .optional()
        .describe(
            'The position where to place the watermark. Provide this to change watermark position.',
        ),
});
export type SetWatermarkArgs = z.infer<typeof legacySetWatermarkSchema>;

export class LegacySetWatermarkTool extends VideoStateTool<typeof legacySetWatermarkSchema> {
    protected override readonly schema = legacySetWatermarkSchema;
    protected override readonly toolName = TOOL_NAMES.setWatermark;
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    protected override readonly toolDescription =
        'Set watermark for a specific scene: watermark_source_index changes its source, position moves it, and visibility shows or hides it.';

    public override getToolDisplayName(): string {
        return 'Setting watermark';
    }

    public get statusIcon(): string {
        return 'branding_watermark';
    }

    public async execute(args: SetWatermarkArgs, context: ToolExecutionContext) {
        const { scene_index, watermark_source_index, position, visibility } = args;

        // check if the user has the watermark capability
        const canSetWatermark = SERVER?.USER?.capabilities?.custom_watermark ?? false;
        if (!canSetWatermark) {
            return `Error: Current plan does not support custom watermarks. Use ${TOOL_NAMES.suggestUpgrade} tool to show the user that they need to upgrade.`;
        }

        try {
            // Validate scene index
            if (scene_index >= this.luminary.scenes.length) {
                return `Error: scene_index ${scene_index} is out of bounds for the current video`;
            }
            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found at index ${scene_index}`;
            }

            return applySceneWatermarkEdit(
                this.luminary,
                this.dispatch,
                scene,
                String(scene_index),
                { visibility, watermark_source_index, position },
                context.parentActionGroup,
            );
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting watermark: ${errorMessage}`;
        }
    }
}
